import {
  GeoAreasSchema,
  GeoCitiesSchema,
  GeoRegionsSchema,
} from '@contracts/schemas/geo.schema';
import type { AreaQuery, CityQuery, GeoRepository } from '@/application/ports/GeoRepository';
import type { AreaSuggestion, CitySuggestion, CountryEntry, GeoRegion } from '@/domain';
import { request } from '../client';

/** The query of a search for a place: the country always, and what else was asked. */
function placeQuery(entries: Record<string, string | number | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    const text = value == null ? '' : String(value).trim();
    if (text !== '') params.set(key, text);
  }
  return `?${params.toString()}`;
}

/**
 * Geography for an address. The request of 6 October 2026 (fifth), section 2.
 *
 * The countries are a file the app carries, the one the server reads word for
 * word, so there is no call for them. It is imported dynamically, so it is a
 * chunk of its own that arrives when the first form or label asks: the list
 * is forty eight kilobytes and the first page has no use for it. One load for
 * the life of the page.
 *
 * The regions, the cities and the areas are the server's, asked for one
 * country at a time.
 */
export class HttpGeoRepository implements GeoRepository {
  private loaded: Promise<CountryEntry[]> | null = null;

  countries(): Promise<CountryEntry[]> {
    this.loaded ??= import('@/shared/geo/countries.json').then(
      (file) => file.default as unknown as CountryEntry[],
    );
    return this.loaded;
  }

  async regions(country: string): Promise<GeoRegion[]> {
    const result = await request(`/geo/regions${placeQuery({ country })}`, {
      schema: GeoRegionsSchema,
    });
    return result.items;
  }

  async cities(query: CityQuery): Promise<CitySuggestion[]> {
    const result = await request(
      `/geo/cities${placeQuery({
        country: query.country,
        region: query.region,
        q: query.q,
        limit: query.limit,
      })}`,
      { schema: GeoCitiesSchema },
    );
    return result.items;
  }

  async areas(query: AreaQuery): Promise<AreaSuggestion[]> {
    const result = await request(
      `/geo/areas${placeQuery({
        country: query.country,
        city: query.city,
        q: query.q,
        limit: query.limit,
      })}`,
      { schema: GeoAreasSchema },
    );
    return result.items;
  }
}
